// API-Funktionen für Angebote
import axios from 'axios'; // Standard axios importieren
import { OfferFormData, OfferDetailsApiResponse } from '../types/offerTypes';

// Basis-URL für API-Anfragen - Aus Umgebungsvariable oder mit korrektem Pfad als Fallback
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://invoice-service-php-685396932615.europe-west3.run.app';

/**
 * Sendet Angebotsdaten an das Backend, um ein PDF zu generieren.
 * @param offerData Die Angebotsdaten.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Ein Objekt mit dem generierten PDF-Blob und der ID des erstellten Angebots.
 */
export const generateOffer = async (offerData: OfferFormData, token: string): Promise<{ pdfBlob: Blob, offerId: string | null, filename: string | null }> => {
    // Korrekt konstruierter Endpunkt
    const ENDPOINT = `${API_BASE_URL}/generate-offer`;
    
    try {
        console.log('Sende Angebotsanfrage an:', ENDPOINT);
        
        // Headers mit Dokumentsprache
        const headers: { [key: string]: string } = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/pdf'
        };
        
        // NEU: Dokumentsprache im Header für Backend-Logging
        if (offerData.documentLanguage) {
            headers['X-Document-Language'] = offerData.documentLanguage;
        }
        
        // Anfrage mit korrektem Content-Type und Accept-Header
        const response = await axios.post(ENDPOINT, offerData, {
            headers: headers,
            responseType: 'arraybuffer', // Wichtig: ArrayBuffer statt Blob anfordern
        });

        // Extrahiere ID aus Header falls vorhanden
        const offerId = response.headers['x-created-offer-id'] || null;
        
        if (!offerId) {
            console.warn('Did not receive X-Created-Offer-Id header from backend.');
        }

        // Extrahiere Dateinamen aus Content-Disposition Header
        let filename: string | null = null;
        const contentDisposition = response.headers['content-disposition'];
        console.log('Content-Disposition Header:', contentDisposition);
        if (contentDisposition) {
            // Versuche verschiedene Formate: filename="name.pdf" oder filename=name.pdf
            const filenameMatch = contentDisposition.match(/filename="([^"]+)"/i) 
                || contentDisposition.match(/filename=([^;\s]+)/i);
            if (filenameMatch && filenameMatch[1]) {
                filename = filenameMatch[1];
                console.log('Extracted filename:', filename);
            }
        }

        // Konvertiere ArrayBuffer korrekt in einen PDF-Blob mit explizitem MIME-Typ
        const pdfBlob = new Blob([response.data], { 
            type: 'application/pdf' 
        });
        
        return { pdfBlob, offerId, filename };
    } catch (error: any) {
        // Verbesserte Fehlerbehandlung für verschiedene Fehlerfälle
        if (error.response) {
            // Der Server hat mit einem Fehlercode geantwortet
            const errorMessage = error.response.data instanceof ArrayBuffer
                ? "Backend-Fehler: " + new TextDecoder().decode(error.response.data)
                : error.response.data?.error || `HTTP-Fehler: ${error.response.status}`;
            
            throw new Error(errorMessage);
        } else if (error.request) {
            // Die Anfrage wurde gesendet, aber keine Antwort erhalten
            throw new Error(`Keine Antwort vom Server: ${error.message}`);
        } else {
            // Fehler beim Einrichten der Anfrage
            throw new Error(`Anfragefehler: ${error.message}`);
        }
    }
};

// --- NEUE FUNKTIONEN für Angebotsliste und Details ---

/**
 * Ruft die Liste der Angebote für den aktuellen Benutzer ab.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Eine Liste von Angebots-Metadaten.
 */
export const listOffers = async (token: string): Promise<any[]> => { // TODO: Stärker typisieren mit OfferListItem oder ähnlichem
    const ENDPOINT = `${API_BASE_URL}/offers`; // Verwende die Basis-URL
    console.log(`Rufe Angebotsliste von ${ENDPOINT} ab`);
    try {
        const response = await axios.get(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            timeout: 15000 // 15 Sekunden Timeout
        });
        return response.data; 
    } catch (error: any) {
        console.error("Fehler beim Abrufen der Angebotsliste:", error);
        if (error.response) {
             console.error("Status:", error.response.status);
             console.error("Daten:", error.response.data);
        }
        throw new Error('Angebotsliste konnte nicht geladen werden.');
    }
};

/**
 * Ruft die Detaildaten eines spezifischen Angebots ab.
 * @param offerId Die ID des Angebots.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Die Angebotsdaten (vermutlich das rawData-Objekt).
 */
export const getOfferDetails = async (offerId: string, token: string): Promise<OfferDetailsApiResponse> => {
    const ENDPOINT = `${API_BASE_URL}/offers/${offerId}`; // Verwende die Basis-URL
    console.log(`Rufe Angebotsdetails von ${ENDPOINT} ab`);
    try {
        const response = await axios.get<OfferDetailsApiResponse>(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            timeout: 15000 // 15 Sekunden Timeout
        });
        // TODO: Validieren, ob die zurückgegebenen Daten dem OfferFormData-Typ entsprechen
        return response.data;
    } catch (error: any) {
        console.error(`Fehler beim Abrufen der Angebotsdetails für ID ${offerId}:`, error);
         if (error.response) {
             console.error("Status:", error.response.status);
             console.error("Daten:", error.response.data);
             if (error.response.status === 404) {
                 throw new Error('Angebot nicht gefunden oder Zugriff verweigert.');
             }
        }
        throw new Error('Angebotsdetails konnten nicht geladen werden.');
    }
};

// --- DRAFT MANAGEMENT FUNKTIONEN ---

/**
 * Speichert ein Angebots-Draft (ohne offizielle Angebotsnummer).
 * @param offerData Die Angebotsdaten als Draft.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Ein Objekt mit der Draft-ID.
 */
export const saveDraft = async (offerData: OfferFormData, token: string): Promise<{ draftId: string }> => {
    const ENDPOINT = `${API_BASE_URL}/offer-drafts`;
    
    try {
        console.log('Speichere Angebots-Draft:', ENDPOINT);
        
        const response = await axios.post(ENDPOINT, offerData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
        });

        // Backend sendet { success: true, data: { id: "...", ... } }
        const draftId = response.data?.data?.id || response.data.draftId || response.headers['x-created-draft-id'];
        
        if (!draftId) {
            console.error('Unexpected response format:', response.data);
            throw new Error('Keine Draft-ID vom Backend erhalten.');
        }

        return { draftId };
    } catch (error: any) {
        console.error('Fehler beim Speichern des Drafts:', error);
        if (error.response) {
            const errorMessage = error.response.data?.error || `HTTP-Fehler: ${error.response.status}`;
            throw new Error(errorMessage);
        } else if (error.request) {
            throw new Error(`Keine Antwort vom Server: ${error.message}`);
        } else {
            throw new Error(`Anfragefehler: ${error.message}`);
        }
    }
};

/**
 * Generiert ein Angebots-Preview PDF ohne offizielle Nummernvergabe.
 * @param offerData Die Angebotsdaten.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Ein Objekt mit dem Preview PDF-Blob und der Draft-ID.
 */
export const generateOfferPreview = async (offerData: OfferFormData, token: string): Promise<{ pdfBlob: Blob, draftId: string }> => {
    const ENDPOINT = `${API_BASE_URL}/generate-offer-preview`;
    
    try {
        console.log('Generiere Angebots-Preview:', ENDPOINT);
        
        const response = await axios.post(ENDPOINT, offerData, {
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
                'Accept': 'application/pdf'
            },
            responseType: 'arraybuffer',
        });

        const draftId = response.headers['x-created-draft-id'];
        
        if (!draftId) {
            throw new Error('Keine Draft-ID vom Backend erhalten.');
        }

        const pdfBlob = new Blob([response.data], { 
            type: 'application/pdf' 
        });
        
        return { pdfBlob, draftId };
    } catch (error: any) {
        console.error('Fehler beim Generieren des Preview:', error);
        if (error.response) {
            const errorMessage = error.response.data instanceof ArrayBuffer
                ? "Backend-Fehler: " + new TextDecoder().decode(error.response.data)
                : error.response.data?.error || `HTTP-Fehler: ${error.response.status}`;
            
            throw new Error(errorMessage);
        } else if (error.request) {
            throw new Error(`Keine Antwort vom Server: ${error.message}`);
        } else {
            throw new Error(`Anfragefehler: ${error.message}`);
        }
    }
};

/**
 * Ruft die Liste der Angebots-Drafts für den aktuellen Benutzer ab.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Eine Liste von Draft-Metadaten.
 */
export const listOfferDrafts = async (token: string): Promise<any[]> => {
    const ENDPOINT = `${API_BASE_URL}/offer-drafts`;
    console.log(`Rufe Angebots-Drafts von ${ENDPOINT} ab`);
    
    try {
        const response = await axios.get(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            timeout: 15000
        });
        
        // Backend sendet { success: true, data: [...] } - extrahiere das data Array
        if (response.data.success && Array.isArray(response.data.data)) {
            return response.data.data;
        } else {
            console.warn('Unexpected response format:', response.data);
            return []; // Fallback zu leerem Array
        } 
    } catch (error: any) {
        console.error("Fehler beim Abrufen der Angebots-Drafts:", error);
        if (error.response) {
             console.error("Status:", error.response.status);
             console.error("Daten:", error.response.data);
        }
        throw new Error('Angebots-Drafts konnten nicht geladen werden.');
    }
};

/**
 * Ruft die Detaildaten eines spezifischen Angebots-Drafts ab.
 * @param draftId Die ID des Drafts.
 * @param token Firebase ID-Token für die Authentifizierung.
 * @returns Die Draft-Daten.
 */
export const getDraftDetails = async (draftId: string, token: string): Promise<any> => {
    const ENDPOINT = `${API_BASE_URL}/offer-drafts/${draftId}`;
    console.log(`Rufe Draft-Details von ${ENDPOINT} ab`);
    
    try {
        const response = await axios.get(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            timeout: 15000
        });
        
        // Backend sendet { success: true, data: {...} } - extrahiere das data Objekt
        if (response.data.success && response.data.data) {
            return response.data.data;
        } else {
            console.warn('Unexpected response format:', response.data);
            return response.data; // Fallback
        }
    } catch (error: any) {
        console.error(`Fehler beim Abrufen der Draft-Details für ID ${draftId}:`, error);
         if (error.response) {
             console.error("Status:", error.response.status);
             console.error("Daten:", error.response.data);
             if (error.response.status === 404) {
                 throw new Error('Draft nicht gefunden oder Zugriff verweigert.');
             }
        }
        throw new Error('Draft-Details konnten nicht geladen werden.');
    }
};

/**
 * Löscht einen Angebots-Draft.
 * @param draftId Die ID des zu löschenden Drafts.
 * @param token Firebase ID-Token für die Authentifizierung.
 */
export const deleteDraft = async (draftId: string, token: string): Promise<void> => {
    const ENDPOINT = `${API_BASE_URL}/offer-drafts/${draftId}`;
    console.log(`Lösche Draft ${draftId} bei ${ENDPOINT}`);
    
    try {
        await axios.delete(ENDPOINT, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            timeout: 15000
        });
    } catch (error: any) {
        console.error(`Fehler beim Löschen des Drafts ${draftId}:`, error);
        if (error.response && error.response.status !== 404) {
            throw new Error('Draft konnte nicht gelöscht werden.');
        }
        // 404 ignorieren, da Draft bereits gelöscht ist
    }
};

// TODO: Funktionen wie getOffers, getOffer für Archiv- und Folge-Features hinzufügen 